import { useEffect, useState } from 'react';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Link } from 'react-router-dom';
import PropertyItem from '../components/PropertyItem';
import Slider from '../components/Slider';
import SearchBar from '../components/SearchBar';
import Testimonials from '../components/Testimonials';

export default function Home() {
  const [offerListings, setOfferListings] = useState(null);
  const [rentListings, setRentListings] = useState(null);
  const [saleListings, setSaleListings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchListings() {
      try {
        // Fetch all listings in parallel with timeout
        const timeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), 5000)
        );

        const fetchOffers = async () => {
          const listingsRef = collection(db, "listings");
          const q = query(
            listingsRef,
            where("offer", "==", true),
            orderBy("timestamp", "desc"),
            limit(4)
          );
          const querySnap = await getDocs(q);
          const listings = [];
          querySnap.forEach((doc) => {
            listings.push({ id: doc.id, data: doc.data() });
          });
          return listings;
        };

        const fetchRent = async () => {
          const listingsRef = collection(db, "listings");
          const q = query(
            listingsRef,
            where("type", "==", "rent"),
            orderBy("timestamp", "desc"),
            limit(4)
          );
          const querySnap = await getDocs(q);
          const listings = [];
          querySnap.forEach((doc) => {
            listings.push({ id: doc.id, data: doc.data() });
          });
          return listings;
        };

        const fetchSale = async () => {
          const listingsRef = collection(db, "listings");
          const q = query(
            listingsRef,
            where("type", "==", "sale"),
            orderBy("timestamp", "desc"),
            limit(4)
          );
          const querySnap = await getDocs(q);
          const listings = [];
          querySnap.forEach((doc) => {
            listings.push({ id: doc.id, data: doc.data() });
          });
          return listings;
        };

        // Fetch all with timeout protection
        const [offers, rent, sale] = await Promise.race([
          Promise.all([
            fetchOffers().catch(() => []),
            fetchRent().catch(() => []),
            fetchSale().catch(() => []),
          ]),
          timeout,
        ]).catch(() => [[], [], []]);

        setOfferListings(offers);
        setRentListings(rent);
        setSaleListings(sale);
      } catch (error) {
        console.log("Error fetching listings:", error);
        setOfferListings([]);
        setRentListings([]);
        setSaleListings([]);
      } finally {
        setLoading(false);
      }
    }
    fetchListings();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <Slider />
      
      {/* Hero Section with Search Bar */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Find Your Dream Property
            </h1>
            <p className="text-xl text-blue-100">
              Search from thousands of verified properties across India
            </p>
          </div>
          <SearchBar />
        </div>
      </div>

      <div className="max-w-6xl mx-auto pt-8 space-y-6 px-4">
        {offerListings && offerListings.length > 0 && (
          <>
            <div className="m-2 mb-6">
              <h2 className="px-3 text-2xl mt-6 font-semibold">Recent offers</h2>
              <Link to="/offers">
                <p className="px-3 text-sm text-blue-600 hover:text-blue-800 transition duration-150 ease-in-out">
                  Show more offers
                </p>
              </Link>
            </div>
            <ul className="sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {offerListings.map((listing) => (
                <PropertyItem
                  key={listing.id}
                  listing={listing.data}
                  id={listing.id}
                />
              ))}
            </ul>
          </>
        )}

        {rentListings && rentListings.length > 0 && (
          <>
            <div className="m-2 mb-6">
              <h2 className="px-3 text-2xl mt-6 font-semibold">Places for rent</h2>
              <Link to="/category/rent">
                <p className="px-3 text-sm text-blue-600 hover:text-blue-800 transition duration-150 ease-in-out">
                  Show more places for rent
                </p>
              </Link>
            </div>
            <ul className="sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {rentListings.map((listing) => (
                <PropertyItem
                  key={listing.id}
                  listing={listing.data}
                  id={listing.id}
                />
              ))}
            </ul>
          </>
        )}

        {saleListings && saleListings.length > 0 && (
          <>
            <div className="m-2 mb-6">
              <h2 className="px-3 text-2xl mt-6 font-semibold">Places for sale</h2>
              <Link to="/category/sale">
                <p className="px-3 text-sm text-blue-600 hover:text-blue-800 transition duration-150 ease-in-out">
                  Show more places for sale
                </p>
              </Link>
            </div>
            <ul className="sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {saleListings.map((listing) => (
                <PropertyItem
                  key={listing.id}
                  listing={listing.data}
                  id={listing.id}
                />
              ))}
            </ul>
          </>
        )}

        {(!offerListings || offerListings.length === 0) &&
          (!rentListings || rentListings.length === 0) &&
          (!saleListings || saleListings.length === 0) && (
            <div className="text-center py-20">
              <h2 className="text-3xl font-bold text-gray-700 mb-4">No Properties Yet</h2>
              <p className="text-gray-500 mb-6">Be the first to list a property!</p>
              <Link to="/profile">
                <button className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition">
                  Create Your First Listing
                </button>
              </Link>
            </div>
          )}
      </div>
      <Testimonials />
    </div>
  );
}
